import { DataSource, LookerAppInfo } from "@coeff/api";
import {
  COLORS,
  DataSourceTypeDisplayNames,
  ERROR_TITLES,
  WARNING_MESSAGES,
  WARNING_TITLES,
} from "@coeff/utils";
import { useMutation } from "@tanstack/react-query";
import { Footer } from "antd/lib/layout/layout";
import React, { useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { FlexColumn, FlexRow } from "../../../common";
import { Button } from "../../../common/Button";
import { Loader } from "../../../common/Loader";
import { Radio } from "../../../common/Radio";
import { WarningMessage } from "../../../common/WarningMessage";
import { useApiClient, useMessage } from "../../../globals/contexts";
import { sentryCapture } from "../../utils";

import { LookerConnectionInfo } from "./AuthorizeLookerContentV2";
import { AuthType, OAUTH_ACTION_NAMES, OAUTH_OPTIONS } from "./constants";
import { useOAuth } from "./useOAuth.hook";

const Tag = styled(Typography)`
  background: ${COLORS.yellow};
  padding: 2px 4px;
  border-radius: 4px;
  width: fit-content;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;

  position: absolute;
  right: -10px;
`;

const StyledFooter = styled(Footer)`
  position: fixed;
  bottom: 0px;
  z-index: 100;
  width: 100%;
  height: auto;
  background-color: ${COLORS.white};
  padding: 15px;
  padding-bottom: 0;

  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledRadio = styled(Radio)<{
  isActive?: boolean;
}>`
  display: flex;
  height: auto;
  font-size: 14px;
  color: ${COLORS.black85};
  white-space: normal;
  background-color: ${COLORS.black4};
  border-radius: 4px;
  margin: 0;
  margin-bottom: 10px;
  padding: 10px;
  border: 2px solid ${({ isActive }) => (isActive ? COLORS.coeblue4 : COLORS.black4)};

  span {
    &.ant-radio {
      height: 16px;
      position: relative;
      margin-right: 5px;

      .ant-radio-checked {
        &::after {
          animation-duration: 0s !important;
        }
      }
    }
  }
`;

type Props = {
  connectionInfo: LookerConnectionInfo;
  event_props: { email?: string };
  onAuthorize: (dataSource: DataSource) => void;
  onCancel: (dataSource: DataSource) => void;
};

export const OauthStep: React.FC<Props> = ({
  onAuthorize,
  onCancel,
  connectionInfo,
  event_props,
}) => {
  const message = useMessage();

  const api = useApiClient();

  const [oauthClient, setOauthClient] = useState<LookerAppInfo | null>(null);

  const { handleLookerAuthorize, handleLookerCancel, isAuthInProgress, popupBlocked } = useOAuth({
    event_props,
    onAuthorize: datasource => onAuthorize(datasource),
  });

  const [authType, setAuthType] = useState<AuthType>(AuthType.OAUTH);

  const authorizeLookerAppMutation = useMutation({
    mutationFn: async () => {
      if (oauthClient) {
        return oauthClient;
      }

      const response = await api.upsertLookerAppInfo({
        upsert_looker_app_info_request: {
          base_url: connectionInfo.url,
          client_id: connectionInfo.clientId,
          client_secret: connectionInfo.clientSecret,
        },
      });

      const apps = await api.listLookerAppInfo().then(response => response?.data?.apps_list);

      return apps?.find(app => app.client_app_guid === response?.data?.client_app_guid);
    },
    onSuccess: oauthConfig => {
      if (!oauthConfig) {
        throw new Error("upsertLookerAppInfo API returned invalid response");
      }
      setOauthClient(oauthConfig);
      handleLookerAuthorize({ appInfo: oauthConfig, isEmbeded: true });
    },
    onError: error => {
      message.error("Failed to create Looker connection");
      sentryCapture({
        error,
        name: "AuthorizeLooker: POST /api/data_sources/looker/apps/upsert",
      });
    },
  });

  const createDataSourceMutation = useMutation({
    mutationFn: () => {
      return api.createDataSource({
        create_data_source_request: {
          data_source: {
            data_source_type: "looker",
            data_source_name: DataSourceTypeDisplayNames.looker,
            credentials: {
              client_id: connectionInfo.clientId,
              client_secret: connectionInfo.clientSecret,
              url: connectionInfo.url,
            },
            is_shared_with_domain: false,
          },
        },
      });
    },
    onSuccess: response => {
      const nextDataSource = response.data.data_source;
      onCancel(nextDataSource);
    },
    onError: error => {
      message.error("Failed to create data source");

      sentryCapture({
        error,
        name: "AuthorizeLooker: POST /api/data_sources",
      });
    },
  });

  const handleNext = () => {
    if (authType === AuthType.OAUTH) {
      authorizeLookerAppMutation.mutate();
    } else {
      createDataSourceMutation.mutate();
    }
  };

  const handleCancel = () => {
    handleLookerCancel();
  };

  const disabled =
    isAuthInProgress || authorizeLookerAppMutation.isLoading || createDataSourceMutation.isLoading;

  return (
    <StyledFooter>
      {popupBlocked && (
        <WarningMessage
          title={WARNING_TITLES.POPUP_BLOCKED}
          message={WARNING_MESSAGES.POPUP_BLOCKED}
          style={{ marginBottom: 10 }}
        />
      )}

      <Typography fontWeight={700} fontSize={18} lineHeight={"21px"}>
        Enable Looker OAuth for your domain
      </Typography>

      <div style={{ marginTop: 10 }}>
        {OAUTH_OPTIONS.map(options => (
          <StyledRadio
            value={options.value}
            key={options.title}
            checked={options.value === authType}
            disabled={disabled}
            onChange={event => setAuthType(event.target.value)}
            isActive={options.value === authType}
          >
            <FlexRow gap={0} style={{ justifyContent: "space-between", position: "relative" }}>
              <Typography fontWeight={700} fontSize={14} lineHeight={"17px"} color="textPrimary">
                {options.title}
              </Typography>
              {options.isRecommended && <Tag>Recommended</Tag>}
            </FlexRow>
            <Typography
              color="textTertiary"
              fontSize={14}
              fontWeight={500}
              lineHeight={"18px"}
              style={{ marginTop: 6, color: COLORS.black50 }}
            >
              {options.description}
            </Typography>
          </StyledRadio>
        ))}
      </div>

      <FlexColumn style={{ alignItems: "normal", margin: "5px 30px" }} gap={0}>
        <Button type="primary" disabled={disabled} onClick={handleNext}>
          {disabled ? (
            <Loader
              color="white"
              size="micro"
              style={{
                position: "relative",
                top: "2px",
              }}
            />
          ) : (
            OAUTH_ACTION_NAMES[authType] || "Next"
          )}
        </Button>

        <Button type="link" onClick={isAuthInProgress ? handleCancel : undefined}>
          {isAuthInProgress ? "Cancel" : null}
        </Button>
      </FlexColumn>
    </StyledFooter>
  );
};
